import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Label,
  Spinner,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { useSubscriptionEntitlements } from "@query";
import { APP_BASE_PATH } from "@shared";
import type { ISubscriptionRow } from "./types";

interface ISubscriptionExpandedRowProps {
  row: ISubscriptionRow;
}

function formatDate(value: string | undefined): string {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

export function SubscriptionExpandedRow({
  row,
}: ISubscriptionExpandedRowProps) {
  const { t } = useTranslation();
  const { data: categories, isLoading } = useSubscriptionEntitlements(
    row.id,
    APP_BASE_PATH,
  );

  const mainInfo = [
    { label: "Company Name*", value: row.originalData?.companyName ?? "—" },
    { label: "Country", value: row.originalData?.countryCode ?? "—" },
    {
      label: "Billing Profile ID*",
      value: row.originalData?.billingProfileId ?? "—",
    },
    {
      label: `${t("hub.subscription.software_start_date")}*`,
      value: formatDate(
        row.originalData?.softwareStartDate ??
          row.originalData?.billingStartDate,
      ),
    },
    {
      label: `${t("hub.subscription.software_end_date")}*`,
      value: formatDate(
        row.originalData?.softwareEndDate ?? row.originalData?.billingEndDate,
      ),
    },
    {
      label: t("hub.subscription.billing_eligible_date"),
      value: formatDate(row.originalData?.billingEligibleDate),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const allCategories = categories ?? [];
  const defaultValues = ["main-info", ...allCategories.map((c) => c.id)];

  return (
    <div className="p-4 bg-muted/20">
      <Accordion
        type="multiple"
        variant="outline"
        embedded
        defaultValue={defaultValues}
        className="flex flex-col gap-4">
        <AccordionItem value="main-info" className="bg-background">
          <AccordionTrigger className="hover:no-underline py-4 font-semibold">
            {t("hub.subscription.main_information", {
              defaultValue: "Main Information",
            })}
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-5 gap-4">
              {mainInfo.map((info) => (
                <div key={info.label} className="flex flex-col gap-1">
                  <Label className="text-xs text-muted-foreground font-medium">
                    {info.label}
                  </Label>
                  <Label
                    className="text-sm font-normal truncate"
                    title={info.value}>
                    {info.value}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {allCategories.map((category) => (
          <AccordionItem
            key={category.id}
            value={category.id}
            className="bg-background">
            <AccordionTrigger className="hover:no-underline py-4 font-semibold">
              {category.categoryName}
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="grid grid-cols-5 gap-4">
                {category.entitlements.map((entitlement) => (
                  <div key={entitlement.id} className="flex flex-col gap-1">
                    <Label className="text-xs text-muted-foreground font-medium">
                      {entitlement.name}
                      {entitlement.isMandatory ? "*" : ""}
                    </Label>
                    <Label className="text-sm font-normal truncate">
                      {entitlement.entitlementType === "METRICS"
                        ? entitlement.unitValue
                        : entitlement.valueDefinition || "—"}
                    </Label>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
